import type { Maybe } from '@adornis/base/utilTypes.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/design/components/cr-data-view-table.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-dropdown-search-selection.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { of } from 'rxjs';
import { type WorkTrackingChange, WorkTrackingValDiff } from '../entitites/WorkTrackingChange.js';

@customElement('chancen-dialog-work-tracking-history')
export class DialogWorkTrackingHistory extends ChromiumDialog<void> {
  static override get element_name() {
    return 'chancen-dialog-work-tracking-history';
  }

  @property({ attribute: false }) public changes = new RXController<Maybe<WorkTrackingChange[]>>(this, []);
  @property({ attribute: false }) public nameFn = new RXController(this, (val: string) => val);

  override heading = 'Änderungshistorie';

  override content() {
    return html` <cr-flex
      space="md"
      ${css({
        display: 'block',
        minWidth: '700px',
        padding: `${this.spacing.sm} ${this.spacing.md}`,
        overflow: 'auto',
        maxHeight: '600px',
      })}
    >
      ${(this.changes.value ?? []).toReversed().map((c, i) => this._change(c))}
    </cr-flex>`;
  }

  private _change(c: WorkTrackingChange) {
    return html`
      <cr-card raised ${css({ marginBottom: this.spacing.md })}>
        <cr-flex space="md">
          <chancen-component-card-title>
            Änderung am ${c.date?.toFormat('dd.MM.yyyy HH:mm')}
          </chancen-component-card-title>
          <cr-grid columns="100px 1fr" space="md">
            <cr-text bold>Person:</cr-text>
            <cr-text>${c.from}</cr-text>
            <cr-text bold>Notiz:</cr-text>
            <cr-text>${c.note ?? 'Keine notiz angegeben...'}</cr-text>
          </cr-grid>
          ${(c.diff?.length ?? 0) > 0
            ? html` <x-data-view
                .options=${{
                  class: WorkTrackingValDiff,
                  operation: (skip, limit) => {
                    return fields => {
                      if (skip > 0) {
                        return [];
                      }
                      return of(c.diff);
                    };
                  },
                  onlyShowLastTranslation: true,
                  customColumns: [
                    { fieldName: 'val', columnName: 'Feld', getFieldMapping: d => this.nameFn.value(d.val ?? '') },
                    { fieldName: 'from', columnName: 'Von' },
                    { fieldName: 'to', columnName: 'Zu' },
                  ],
                } as DataViewOptions<typeof WorkTrackingValDiff>}
              ></x-data-view>`
            : nothing}
        </cr-flex>
      </cr-card>
    `;
  }
}
